import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import CropFreeIcon from "@mui/icons-material/CropFree";
import GestureIcon from "@mui/icons-material/Gesture";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import RouteIcon from "@mui/icons-material/Route";
import { MapDeliveryJob, MapSelectionTool } from "app/types/mapScreen";
import { SelectedDeliveriesList } from "app/components/Shared/SelectedDeliveriesList";

const TOOL_OPTIONS: Array<{
  id: MapSelectionTool;
  label: string;
  icon: React.ReactNode;
}> = [
  { id: "pointer", label: "Pointer", icon: <AdsClickIcon sx={{ fontSize: 18 }} /> },
  { id: "rectangle", label: "Rectangle zone", icon: <CropFreeIcon sx={{ fontSize: 18 }} /> },
  { id: "lasso", label: "Lasso zone", icon: <GestureIcon sx={{ fontSize: 18 }} /> },
];

const TOOL_RAIL_BUTTON_SX = {
  width: 40,
  height: 40,
  borderRadius: "10px",
  border: "1px solid #e5e9ef",
  bgcolor: "#fff",
  color: "#64748b",
  "&:hover": {
    bgcolor: "#f8fafc",
    borderColor: "#cbd5e1",
  },
};

type MapScreenSelectionPanelProps = {
  jobCount: number;
  assignedCount: number;
  selectedJobs: MapDeliveryJob[];
  selectionTool: MapSelectionTool;
  userTimezone: string;
  onSelectionToolChange: (tool: MapSelectionTool) => void;
  onAssignSelected: () => void;
  onClearSelection: () => void;
  onRemoveSelected: (jobId: string) => void;
  onHighlightJob: (jobId: string | null) => void;
  canAssign: boolean;
};

export function MapScreenSelectionPanel({
  jobCount,
  assignedCount,
  selectedJobs,
  selectionTool,
  userTimezone,
  onSelectionToolChange,
  onAssignSelected,
  onClearSelection,
  onRemoveSelected,
  onHighlightJob,
  canAssign,
}: MapScreenSelectionPanelProps) {
  const selectedCount = selectedJobs.length;

  const selectedDeliveryItems = useMemo(
    () =>
      selectedJobs.map((job) => ({
        id: job.id,
        pickingNumber: job.pickingNumber,
        name: job.name,
        partnerName: job.partnerName || job.customerName,
        scheduledDate: job.scheduledDate,
      })),
    [selectedJobs]
  );

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          zIndex: 500,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          p: 0.75,
          borderRadius: "14px",
          border: "1px solid #e5e9ef",
          bgcolor: "rgba(255,255,255,0.96)",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.1)",
        }}
        className="map-screen-overlay-panel"
      >
        {TOOL_OPTIONS.map((tool) => (
          <Tooltip key={tool.id} title={tool.label} placement="right">
            <IconButton
              size="small"
              aria-label={tool.label}
              onClick={() => onSelectionToolChange(tool.id)}
              sx={{
                ...TOOL_RAIL_BUTTON_SX,
                ...(selectionTool === tool.id
                  ? {
                      bgcolor: "#f0fdf4",
                      borderColor: "#86efac",
                      color: "#166534",
                      boxShadow: "0 2px 8px rgba(95, 166, 122, 0.18)",
                    }
                  : {}),
              }}
            >
              {tool.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          zIndex: 500,
          width: { xs: 240, sm: 280 },
          borderRadius: "14px",
          border: "1px solid #e5e9ef",
          bgcolor: "rgba(255,255,255,0.96)",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.1)",
          overflow: "hidden",
        }}
        className="map-screen-overlay-panel"
      >
        <Box sx={{ px: 1.75, py: 1.25, borderBottom: "1px solid #eef1f4" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: "#0f172a" }}>
            Select deliveries
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: "#64748b", mt: 0.25 }}>
            Unassigned jobs can be selected. Assigned route stops are view-only.
          </Typography>
        </Box>

        <Box
          sx={{
            px: 1.75,
            py: 1.25,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1,
              py: 0.65,
              borderRadius: "10px",
              bgcolor: "#f8fafc",
              border: "1px solid #eef1f4",
            }}
          >
            <PlaceOutlinedIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
            <Box>
              <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8", lineHeight: 1.2 }}>
                Unassigned
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#334155" }}>
                {jobCount}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1,
              py: 0.65,
              borderRadius: "10px",
              bgcolor: "#eff6ff",
              border: "1px solid #dbeafe",
            }}
          >
            <RouteIcon sx={{ fontSize: 16, color: "#2563eb" }} />
            <Box>
              <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8", lineHeight: 1.2 }}>
                Assigned
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#1d4ed8" }}>
                {assignedCount}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ gridColumn: "1 / -1" }}>
            <SelectedDeliveriesList
              items={selectedDeliveryItems}
              userTimezone={userTimezone}
              onRemove={(id) => onRemoveSelected(String(id))}
              onHighlight={(id) => onHighlightJob(id == null ? null : String(id))}
            />
          </Box>
        </Box>

        <Divider sx={{ borderColor: "#eef1f4" }} />

        <Box sx={{ px: 1.75, py: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={{ fontSize: "0.72rem", color: "#64748b" }}>
            Active tool:{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#334155" }}>
              {TOOL_OPTIONS.find((tool) => tool.id === selectionTool)?.label}
            </Box>
          </Typography>
          {selectionTool !== "pointer" && (
            <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8", lineHeight: 1.45 }}>
              Scroll to zoom. Use arrow buttons to move the map. Click and drag to draw a
              selection zone.
            </Typography>
          )}

          <Button
            size="small"
            variant="contained"
            disabled={!canAssign}
            onClick={onAssignSelected}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "#7ec597",
              boxShadow: "none",
              "&:hover": { bgcolor: "#6db486", boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
            }}
          >
            Assign selected
          </Button>

          <Button
            size="small"
            variant="outlined"
            disabled={selectedCount === 0}
            onClick={onClearSelection}
            sx={{
              textTransform: "none",
              borderColor: "#e2e8f0",
              color: "#64748b",
              bgcolor: "#fff",
            }}
          >
            Clear selection
          </Button>
        </Box>
      </Paper>
    </>
  );
}
